import {
	RequestMarketplaceProductRequest,
	MarketplaceSubscriptionResponse,
	MarketplaceVendorSubscription,
	CreateMarketplaceVendorReferralRequest,
	MarketplaceCollection,
	VendorInviteSignupMetadata,
	AcceptPartnerProgramInviteRequest
} from "@/Contracts/Marketplace";
import { ChargebeeInvoiceResponse } from "@/Contracts/Users";
import {
	MarketplaceProduct,
	MarketplaceProductsResponse,
	MarketplaceCategory
} from "@/Contracts/Marketplace/Product";
import {
	VendorCompany,
	VendorCompaniesResponse,
	UnsuspendVendorRequest
} from "@/Contracts/Marketplace/Vendor";
import axios, { AxiosResponse } from "axios";
import {
	getRequest,
	postRequest,
	putRequest
} from "@/Utils/Service/BackendService";
import {
	includeChildEntities,
	ChildEntityHydrationParams
} from "@/Contracts/Utils";

const getMarketplaceProductsUrl = "/v1/marketplace/products";
const getMarketplaceProductDetailsUrl = "/v1/marketplace/products/{product_id}";
const requestMarketplaceProductUrl =
	"/v1/marketplace/products/{product_id}/submit-form";
const getMarketplaceVendorsUrl = "/v1/marketplace/vendors";
const getMarketplaceSubscriptionsUrl = "/v1/marketplace/subscriptions";
const updateClientSubscriptionUrl =
	"v1/marketplace/subscriptions/{subsciption_id}";
const getMarketplaceInvoicesUrl = "/v1/marketplace/invoices";
const createMarketplaceVendorReferralUrl = "/v1/marketplace/vendor-referral";
const vendorInviteSignupMetadataUrl =
	"/v1/marketplace/vendor/{vendor_id}/metadata";
const acceptPartnerProgramInviteUrl =
	"/v1/marketplace/vendor/partner/accept-invite";
const getMarketplaceVendorDetailsUrl = "/v1/marketplace/vendors/{vendor_id}";
const getMarketplaceVendorProductDetailsUrl =
	"/v1/marketplace/vendor/:vendorId/products";
const joinVendorProgramUrl = "/v1/marketplace/vendors/{vendor_id}/join";
const unsuspendVendorURL =
	"/v1/marketplace/vendors/:vendorId/unsuspend/request";

export const getMarketplaceProducts = async (
	collection?: MarketplaceCollection
): Promise<MarketplaceProductsResponse> => {
	try {
		const response: AxiosResponse<MarketplaceProductsResponse> =
			await axios(
				getRequest(
					collection
						? `${getMarketplaceProductsUrl}?included_child_entities=${collection}:all`
						: getMarketplaceProductsUrl,
					null,
					true,
					false,
					false,
					undefined,
					undefined
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getMarketplaceProductDetails = async (
	productId: string
): Promise<MarketplaceProduct> => {
	try {
		const response: AxiosResponse<MarketplaceProduct> = await axios(
			getRequest(
				getMarketplaceProductDetailsUrl.replace(
					"{product_id}",
					productId
				),
				null,
				true,
				false,
				false,
				undefined,
				undefined
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getMarketplaceVendors =
	async (): Promise<VendorCompaniesResponse> => {
		try {
			const response: AxiosResponse<VendorCompaniesResponse> =
				await axios(
					getRequest(
						getMarketplaceVendorsUrl,
						null,
						true,
						false,
						false,
						undefined,
						undefined
					)
				);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const requestMarketplaceProduct = async (
	productId: string,
	requestMarketplaceProductRequest: RequestMarketplaceProductRequest
): Promise<void> => {
	try {
		const response = await axios(
			postRequest(
				requestMarketplaceProductUrl.replace("{product_id}", productId),
				requestMarketplaceProductRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const joinVendorProgram = async (vendorId: string): Promise<void> => {
	try {
		const response = await axios(
			postRequest(
				joinVendorProgramUrl.replace("{vendor_id}", vendorId),
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getMarketplaceSubscriptions =
	async (): Promise<MarketplaceSubscriptionResponse> => {
		try {
			const response: AxiosResponse<MarketplaceSubscriptionResponse> =
				await axios(
					getRequest(
						getMarketplaceSubscriptionsUrl,
						null,
						true,
						false,
						false,
						undefined,
						undefined
					)
				);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const getMarketplaceInvoices =
	async (): Promise<ChargebeeInvoiceResponse> => {
		try {
			const response: AxiosResponse<ChargebeeInvoiceResponse> =
				await axios(
					getRequest(
						getMarketplaceInvoicesUrl,
						null,
						true,
						false,
						false,
						undefined,
						undefined
					)
				);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const updateClientSubscriptions = async (
	clientId: string,
	subsciptionId: string
): Promise<MarketplaceVendorSubscription> => {
	try {
		const payload = { client_company_id: clientId };
		const response: AxiosResponse<MarketplaceVendorSubscription> =
			await axios(
				putRequest(
					updateClientSubscriptionUrl.replace(
						"{subsciption_id}",
						subsciptionId
					),
					payload,
					true,
					false,
					false,
					undefined
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const createMarketplaceVendorReferral = async (
	vendorName: string,
	vendorWebsite: string,
	productCategory: MarketplaceCategory
): Promise<void> => {
	try {
		const createMarketplaceVendorReferralRequest =
			new CreateMarketplaceVendorReferralRequest(
				vendorName,
				vendorWebsite,
				productCategory
			);
		const response = await axios(
			postRequest(
				createMarketplaceVendorReferralUrl,
				createMarketplaceVendorReferralRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getVendorInviteSignupMetadata = async (
	vendorId: string
): Promise<VendorInviteSignupMetadata> => {
	try {
		const url = vendorInviteSignupMetadataUrl.replace(
			"{vendor_id}",
			vendorId
		);
		const response: AxiosResponse<VendorInviteSignupMetadata> = await axios(
			getRequest(url, null, false, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const acceptPartnerProgramInvite = async (
	vendorId: string
): Promise<void> => {
	try {
		const acceptPartnerProgramInviteRequest =
			new AcceptPartnerProgramInviteRequest(vendorId);
		const response = await axios(
			postRequest(
				acceptPartnerProgramInviteUrl,
				acceptPartnerProgramInviteRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getMarketplaceVendorDetails = async (
	vendorId: string
): Promise<VendorCompany> => {
	try {
		const includedChildEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.PARTNER_TIERS_ALL
			)
		);
		const response: AxiosResponse<VendorCompany> = await axios(
			getRequest(
				getMarketplaceVendorDetailsUrl.replace(
					"{vendor_id}",
					vendorId
				) + `?${includedChildEntities}`,
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getMarketplaceVendorProductDetails = async (
	vendorId: string
): Promise<MarketplaceProductsResponse> => {
	try {
		const response: AxiosResponse<MarketplaceProductsResponse> =
			await axios(
				getRequest(
					getMarketplaceVendorProductDetailsUrl.replace(
						":vendorId",
						vendorId
					),
					null,
					true,
					false,
					false
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// TODO: to be removed when marketplace join beta page is removed and marketplace goes fully live
// It will be removed around 1-10 April 2022
// pushes data to airtable
// link to access data: https://airtable.com/shrz4Czu6gZtvVWYy/tblWrYEZsviCwnybE
export const pushBeamerLeadToAirtable = async (
	source: string,
	productName: string,
	userId: string,
	userName: string,
	userEmail: string,
	companyId: string,
	companyName: string,
	subDomain: string
): Promise<void> => {
	try {
		const response = await axios({
			url: "https://api.airtable.com/v0/appXsJIPKFBuIbr0r/Leads",
			method: "post",
			headers: {
				Authorization: "Bearer keyOtG2biuktzUCzL"
			},
			data: {
				records: [
					{
						fields: {
							Name: "test record 2",
							source,
							productName,
							userId,
							userName,
							userEmail,
							companyId,
							companyName,
							subDomain
						}
					}
				]
			}
		});
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const unsuspendVendor = async (
	vendorId: string,
	unsuspendVendorRequest: UnsuspendVendorRequest
): Promise<void> => {
	try {
		const response = await axios(
			postRequest(
				unsuspendVendorURL.replace(":vendorId", vendorId),
				unsuspendVendorRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
